import React, { useEffect, useState, useCallback } from 'react';
import {
  View, Text, StyleSheet, FlatList, TouchableOpacity,
  ActivityIndicator, TextInput,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../../src/api/client';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import { useTheme } from '../../src/context/ThemeContext';
import { AdminToolbar } from '../../src/components/AdminList';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import { fs } from '../../src/utils/responsive';
import type { ColorTheme } from '../../src/utils/theme';

interface LogEntry {
  readonly id: string;
  readonly controllerId: string;
  readonly direction: string;
  readonly topic: string;
  readonly payload: unknown;
  readonly createdAt: string;
}

type DirectionFilter = 'all' | 'inbound' | 'outbound';

/** Max entries for CSV export */
const CSV_EXPORT_LIMIT = 10000;

/** MqttLogsPage - displays filterable, paginated MQTT message logs for all controllers */
export default function MqttLogsPage() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(50);

  // Filters
  const [controllerId, setControllerId] = useState('');
  const [direction, setDirection] = useState<DirectionFilter>('all');
  const [topicFilter, setTopicFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchLogs = useCallback(async (p = 1, signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(limit) });
      if (controllerId.trim()) params.set('controllerId', controllerId.trim());
      if (direction !== 'all') params.set('direction', direction);
      if (topicFilter.trim()) params.set('topic', topicFilter.trim());

      const data = await api<{ data: LogEntry[]; pagination: { total: number } }>(`/api/admin/mqtt-logs?${params}`);
      if (signal?.aborted) return;
      setLogs(data.data);
      setTotal(data.pagination.total);
      setPage(p);
    } catch (err: any) {
      if (!(signal?.aborted)) console.error('Failed to load MQTT logs:', err);
    } finally {
      if (!(signal?.aborted)) setLoading(false);
    }
  }, [controllerId, direction, topicFilter, limit]);

  useEffect(() => { const ac = new AbortController(); fetchLogs(1, ac.signal); return () => ac.abort(); }, []);

  const applyFilters = () => fetchLogs(1);

  const totalPages = Math.ceil(total / limit);

  /** Exports filtered log entries as CSV */
  const exportCsv = async () => {
    const params = new URLSearchParams({ limit: String(CSV_EXPORT_LIMIT), format: 'csv' });
    if (controllerId.trim()) params.set('controllerId', controllerId.trim());
    if (direction !== 'all') params.set('direction', direction);
    if (topicFilter.trim()) params.set('topic', topicFilter.trim());

    try {
      const { exportCsvFromUrl } = await import('../../src/utils/fileExport');
      const { getToken } = await import('../../src/api/client');
      const { config } = await import('../../src/config');
      const token = getToken();
      const url = `${config.apiUrl}/api/admin/mqtt-logs?${params}`;
      await exportCsvFromUrl(url, { Authorization: `Bearer ${token}` }, `mqtt-logs-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err: any) {
      console.error('CSV export failed:', err);
    }
  };

  const renderLog = ({ item }: { item: LogEntry }) => {
    const isExpanded = expandedId === item.id;
    const isInbound = item.direction === 'inbound';
    return (
      <TouchableOpacity
        style={styles.logRow}
        onPress={() => setExpandedId(isExpanded ? null : item.id)}
        activeOpacity={0.7}
      >
        <View style={styles.logHeader}>
          <View style={styles.logLeft}>
            <Ionicons
              name={isInbound ? 'arrow-down-circle' : 'arrow-up-circle'}
              size={18}
              color={isInbound ? '#059669' : '#3b82f6'}
            />
            <Text style={styles.logTopic} numberOfLines={1}>{item.topic}</Text>
          </View>
          <Text style={styles.logTime}>
            {new Date(item.createdAt).toLocaleString()}
          </Text>
        </View>
        <Text style={styles.logControllerId} numberOfLines={1}>
          {item.controllerId}
        </Text>
        {isExpanded && (
          <View style={styles.logPayload}>
            <Text style={styles.logPayloadText}>
              {typeof item.payload === 'string' ? item.payload : JSON.stringify(item.payload, null, 2)}
            </Text>
          </View>
        )}
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
      <Text style={styles.pageTitle}>MQTT Logs</Text>

      {/* Filters */}
      <View style={styles.filterRow}>
        <View style={styles.filterInput}>
          <TextInput
            style={[styles.input, { color: colors.text }]}
            placeholder="Controller ID..."
            placeholderTextColor={colors.textLight}
            value={controllerId}
            onChangeText={setControllerId}
            maxLength={64}
          />
        </View>
        <View style={styles.filterInput}>
          <TextInput
            style={[styles.input, { color: colors.text }]}
            placeholder="Topic filter..."
            placeholderTextColor={colors.textLight}
            value={topicFilter}
            onChangeText={setTopicFilter}
            maxLength={100}
          />
        </View>
        <View style={styles.directionRow}>
          {(['all', 'inbound', 'outbound'] as DirectionFilter[]).map(d => (
            <TouchableOpacity
              key={d}
              style={[styles.dirBtn, direction === d && styles.dirBtnActive]}
              onPress={() => setDirection(d)}
            >
              <Text style={[styles.dirBtnText, direction === d && styles.dirBtnTextActive]}>
                {d === 'all' ? 'All' : d === 'inbound' ? 'In' : 'Out'}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        <TouchableOpacity style={styles.searchBtn} onPress={applyFilters}>
          <Ionicons name="search" size={18} color="#fff" />
          <Text style={styles.searchBtnText}>Search</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.csvBtn} onPress={exportCsv}>
          <Ionicons name="download-outline" size={18} color={colors.primary} />
        </TouchableOpacity>
      </View>

      {/* Results */}
      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      ) : (
        <>
          <Text style={styles.resultCount}>{total} log entries</Text>
          <FlatList ListFooterComponent={<PageFooter />}
            data={logs}
            keyExtractor={(item) => item.id}
            renderItem={renderLog}
            contentContainerStyle={{ paddingBottom: spacing.xl }}
            ListEmptyComponent={
              <Text style={styles.emptyText}>No logs found matching filters</Text>
            }
          />
          {/* Pagination */}
          {totalPages > 1 && (
            <View style={styles.pagination}>
              <TouchableOpacity
                style={[styles.pageBtn, page <= 1 && { opacity: 0.4 }]}
                onPress={() => page > 1 && fetchLogs(page - 1)}
                disabled={page <= 1}
              >
                <Text style={styles.pageBtnText}>Prev</Text>
              </TouchableOpacity>
              <Text style={styles.pageInfo}>Page {page} of {totalPages}</Text>
              <TouchableOpacity
                style={[styles.pageBtn, page >= totalPages && { opacity: 0.4 }]}
                onPress={() => page < totalPages && fetchLogs(page + 1)}
                disabled={page >= totalPages}
              >
                <Text style={styles.pageBtnText}>Next</Text>
              </TouchableOpacity>
            </View>
          )}
        </>
      )}
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, padding: spacing.md },
  pageTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  filterRow: {
    flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.md,
    flexWrap: 'wrap', alignItems: 'center',
  },
  filterInput: { flex: 1, minWidth: 150 },
  input: {
    backgroundColor: c.surface, borderWidth: 1, borderColor: c.borderLight,
    borderRadius: borderRadius.sm, paddingHorizontal: spacing.sm, paddingVertical: 8,
    fontSize: fontSize.sm, fontFamily: 'monospace',
  },
  directionRow: { flexDirection: 'row', borderRadius: borderRadius.sm, overflow: 'hidden', borderWidth: 1, borderColor: c.borderLight },
  dirBtn: { paddingHorizontal: spacing.sm, paddingVertical: 8, backgroundColor: c.surface },
  dirBtnActive: { backgroundColor: c.primary },
  dirBtnText: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.medium },
  dirBtnTextActive: { color: '#fff' },
  searchBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    backgroundColor: c.primary, paddingHorizontal: spacing.md, paddingVertical: 8,
    borderRadius: borderRadius.sm,
  },
  searchBtnText: { color: '#fff', fontSize: fontSize.sm, fontWeight: fontWeight.semibold },
  csvBtn: {
    padding: 8, borderRadius: borderRadius.sm,
    borderWidth: 1, borderColor: c.borderLight, backgroundColor: c.surface,
  },
  resultCount: { fontSize: fontSize.xs, color: c.textSecondary, marginBottom: spacing.sm },
  logRow: {
    backgroundColor: c.surface, borderRadius: borderRadius.sm, padding: spacing.sm,
    marginBottom: 4, borderWidth: 1, borderColor: c.borderLight,
  },
  logHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  logLeft: { flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1 },
  logTopic: { fontSize: fontSize.xs, color: c.text, fontFamily: 'monospace', flex: 1 },
  logTime: { fontSize: fs(10), color: c.textLight },
  logControllerId: { fontSize: fs(10), color: c.textLight, fontFamily: 'monospace', marginTop: 2, marginLeft: 24 },
  logPayload: {
    marginTop: spacing.sm, backgroundColor: c.background, borderRadius: borderRadius.sm,
    padding: spacing.sm,
  },
  logPayloadText: { fontSize: fs(11), color: c.text, fontFamily: 'monospace' },
  emptyText: { textAlign: 'center', color: c.textSecondary, paddingVertical: spacing.xl, fontSize: fontSize.sm },
  pagination: {
    flexDirection: 'row', justifyContent: 'center', alignItems: 'center',
    gap: spacing.md, paddingVertical: spacing.md,
  },
  pageBtn: {
    paddingHorizontal: spacing.md, paddingVertical: 6,
    borderRadius: borderRadius.sm, backgroundColor: c.surface,
    borderWidth: 1, borderColor: c.borderLight,
  },
  pageBtnText: { fontSize: fontSize.sm, color: c.text, fontWeight: fontWeight.medium },
  pageInfo: { fontSize: fontSize.sm, color: c.textSecondary },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
